import { Alert, Button, Disclosure, DisclosurePanel, Separator, Spinner, Typography } from '@compass/components'
import { cn } from '@compass/utils'
import { Link as RouterLink } from '@tanstack/react-router'
import { AppRoute } from 'appRoutes'
import { formatDate, formatTime } from 'features/commonUI'
import { ChevronRightIcon } from 'lucide-react'
import type { IdEventV4 } from 'models/event'
import { type ComponentType, isValidElement, type ReactNode, useState } from 'react'
import { Heading as AriaHeading, Label as AriaLabel, Meter as AriaMeter } from 'react-aria-components'

import type { SuspectScoreBreakdown, SuspectScoreContribution } from './suspectScoreBreakdown'

const VISIBLE_CONTRIBUTIONS = 4
const MIN_BAR_WIDTH_PERCENT = 30

const noop = () => {}

const SUSPECT_SCORE_DESCRIPTION = 'The number of suspicious or fraud-related Smart Signals detected on this event'

export type SuspectScoreBreakdownContentProps = {
  subscriptionId: string
  event?: IdEventV4
  breakdown?: SuspectScoreBreakdown
  isLoading?: boolean
  onInteract?: () => void
}

export function SuspectScoreBreakdownContent({
  subscriptionId,
  event,
  breakdown,
  isLoading,
  onInteract = noop,
}: SuspectScoreBreakdownContentProps) {
  return (
    <div className='flex flex-col'>
      <div className='flex flex-col gap-3 px-4 py-3'>
        <div className='flex flex-col gap-1'>
          <Typography size='base' weight='medium'>
            Suspect Score
          </Typography>
          <Typography size='xs' className='text-gray-800'>
            {SUSPECT_SCORE_DESCRIPTION} based on{' '}
            <RouterLink to={AppRoute.SuspectScore} params={{ subscriptionId }} className='text-orange-700 underline'>
              Suspect Score configuration
            </RouterLink>
            .
          </Typography>
        </div>

        {isLoading || !event ? (
          <>
            <Separator />
            <Spinner className='mx-auto' />
          </>
        ) : breakdown ? (
          <>
            <Breakdown event={event} breakdown={breakdown} onInteract={onInteract} />
            <BreakdownAlert event={event} breakdown={breakdown} />
          </>
        ) : (
          <>
            <Separator />
            <Typography size='sm' className='text-gray-800'>
              A breakdown is unavailable for this event
            </Typography>
          </>
        )}
      </div>

      {event && breakdown ? <BreakdownFooter event={event} breakdown={breakdown} onInteract={onInteract} /> : null}
    </div>
  )
}

function Breakdown({
  event,
  breakdown,
  onInteract,
}: {
  event: IdEventV4
  breakdown: SuspectScoreBreakdown
  onInteract: () => void
}) {
  const [showAll, setShowAll] = useState(false)
  const { contributing, total } = breakdown

  const hiddenCount = contributing.length - VISIBLE_CONTRIBUTIONS
  const isCollapsed = hiddenCount > 1 && !showAll
  const visible = isCollapsed ? contributing.slice(0, VISIBLE_CONTRIBUTIONS) : contributing
  const maxContribution = contributing[0]?.contribution ?? 0

  return (
    <>
      <Separator />

      {contributing.length > 0 ? (
        <Typography size='2xs' weight='medium' transform='uppercase' className='text-gray-800'>
          Breakdown
        </Typography>
      ) : null}

      {contributing.length === 0 ? (
        <Typography size='sm' className='text-gray-800'>
          No signals contributed to this score.
        </Typography>
      ) : (
        <>
          <div className='flex flex-col gap-1.5'>
            {visible.map((item) => (
              <ContributionRow key={item.definition.key} event={event} item={item} max={maxContribution} />
            ))}
            {isCollapsed ? (
              <Button
                variant='ghost'
                size='xs'
                alt
                fullWidth
                onPress={() => {
                  onInteract()
                  setShowAll(true)
                }}
              >
                +{hiddenCount} more
              </Button>
            ) : null}
          </div>

          <div className='flex items-center justify-between gap-2 pr-2'>
            <Typography size='sm' className='text-gray-800'>
              Total from contributing signals
            </Typography>
            <Typography variant='code' size='sm' weight='medium'>
              {total}
            </Typography>
          </div>
        </>
      )}
    </>
  )
}

type IconProps = { className?: string; size?: number | string }

function iconComponent(icon: ReactNode) {
  if (!isValidElement<IconProps>(icon) || typeof icon.type === 'string') {
    return null
  }

  return icon.type as ComponentType<IconProps>
}

function ContributionRow({
  event,
  item,
  max,
  muted,
}: {
  event: IdEventV4
  item: SuspectScoreContribution
  max?: number
  muted?: boolean
}) {
  const { definition, contribution } = item
  const renderedIcon = typeof definition.icon === 'function' ? definition.icon(event) : definition.icon
  const Icon = iconComponent(renderedIcon)

  return (
    <AriaMeter
      value={contribution}
      maxValue={max || 1}
      valueLabel={`+${contribution}`}
      className='relative flex h-6 items-center gap-4 px-2'
    >
      {({ percentage }) => (
        <>
          {!muted && contribution > 0 ? (
            <div
              aria-hidden
              className='absolute inset-y-0 left-0 rounded-sm bg-orange-100'
              style={{ width: `${Math.max(MIN_BAR_WIDTH_PERCENT, percentage)}%` }}
            />
          ) : null}
          <AriaLabel
            className={cn(
              'relative flex min-w-0 items-center gap-2 truncate text-sm',
              muted ? 'text-gray-900' : 'text-orange-700'
            )}
          >
            {Icon ? (
              <Icon size={14} className={cn('shrink-0', muted ? 'text-gray-600' : 'text-orange-700')} />
            ) : (
              renderedIcon
            )}
            {definition.label}
          </AriaLabel>
          <Typography size='sm' variant='code' className='relative ml-auto text-right whitespace-nowrap'>
            <span className='text-gray-800'>+</span>
            {contribution}
          </Typography>
        </>
      )}
    </AriaMeter>
  )
}

function BreakdownFooter({
  event,
  breakdown,
  onInteract,
}: {
  event: IdEventV4
  breakdown: SuspectScoreBreakdown
  onInteract: () => void
}) {
  const items = breakdown.notContributing

  if (items.length === 0) {
    return null
  }

  return (
    <footer
      className='sticky bottom-0 flex flex-col gap-2 bg-gray-100 p-2'
      style={{ boxShadow: '0 -1px 0 0 var(--color-gray-300)' }}
    >
      <NotContributing event={event} items={items} onInteract={onInteract} />
    </footer>
  )
}

function BreakdownAlert({ event, breakdown }: { event: IdEventV4; breakdown: SuspectScoreBreakdown }) {
  const { reconciles, weightsChangedSinceEvent, weightsUpdatedAt, total } = breakdown
  const updatedAtLabel = weightsUpdatedAt ? formatUpdatedAt(weightsUpdatedAt) : undefined

  if (!reconciles) {
    return (
      <Alert size='sm' variant='secondary' className='mb-1'>
        These signals add up to {total} with your current weights, but this event scored {event.suspect_score}.{' '}
        {updatedAtLabel
          ? `The weights were last updated on ${updatedAtLabel}.`
          : 'The weights have likely changed since the event.'}
      </Alert>
    )
  }

  if (weightsChangedSinceEvent) {
    return (
      <Alert size='sm' variant='secondary' className='mb-1'>
        {updatedAtLabel
          ? `Based on your current weights, which were last updated on ${updatedAtLabel}, after this event.`
          : 'Based on your current weights, which were changed after this event.'}
      </Alert>
    )
  }

  return null
}

function formatUpdatedAt(date: Date) {
  return `${formatDate(date, 'shortWithYear')} at ${formatTime(date)}`
}

function NotContributing({
  event,
  items,
  onInteract,
}: {
  event: IdEventV4
  items: SuspectScoreContribution[]
  onInteract: () => void
}) {
  return (
    <Disclosure onExpandedChange={onInteract}>
      {({ isExpanded }) => (
        <>
          <AriaHeading className='contents'>
            <Button
              slot='trigger'
              variant='ghost'
              tone='background2'
              size='sm'
              alt
              fullWidth
              contentContainerClassName='justify-start'
            >
              Detected but not contributing
              <ChevronRightIcon
                className={cn('transition-transform motion-reduce:transition-none', { 'rotate-90': isExpanded })}
              />
            </Button>
          </AriaHeading>
          <DisclosurePanel>
            <div className='flex flex-col gap-1.5 p-2 pb-0'>
              {items.map((item) => (
                <ContributionRow key={item.definition.key} event={event} item={item} muted />
              ))}
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  )
}
